import {
	MobilePushChannel,
	PushChannelsPerUser,
} from "@api-types/push-notifications/PushNotificationsDto";
import {
	GroupedSettings,
	ThreeStateToggleEnum,
} from "@data-types/notification-settings/NotificationSettingsTypes";
import { UserTypes } from "@utils/common";
import { getAgencyId, getUserId } from "@utils/membership";

export function getNotificationItems(data: PushChannelsPerUser) {
	return data.item.map((channel) => ({
		...channel.mobilePushChannel,
		isActive: channel.isActive,
		userId: channel.userId,
		agencyId: channel.agencyId,
	}));
}

export function getNotificationGroupsWithItems(data: MobilePushChannel[]) {
	return data.reduce((acc: GroupedSettings[], setting) => {
		const groupName = setting.channelGroupName;
		let group = acc.find((group) => group.groupName === groupName);

		if (!group) {
			group = {
				groupName: groupName,
				item: [],
				value: ThreeStateToggleEnum.OFF,
			};
			acc.push(group);
		}

		group.item.push(setting);

		return acc;
	}, []);
}

export function sortNotificationGroupsAndItems(
	data: GroupedSettings[],
	groupSortOrder: string[]
) {
	return data
		.sort((a, b) => {
			if (
				groupSortOrder.includes(a.groupName) &&
				groupSortOrder.includes(b.groupName)
			) {
				return (
					groupSortOrder.indexOf(a.groupName) -
					groupSortOrder.indexOf(b.groupName)
				);
			}
			return 1;
		})
		.map((group) => {
			const sortedGroupItems = group.item.sort(
				(a, b) => a.sortOrder - b.sortOrder
			);
			return {
				...group,
				value: getGroupValueFromItems(group),
				item: sortedGroupItems,
			};
		});
}

export function getGroupValueFromItems(group: GroupedSettings) {
	if (group.item.every((item) => item.isActive == true)) {
		return ThreeStateToggleEnum.ON;
	}

	if (group.item.every((item) => item.isActive == false)) {
		return ThreeStateToggleEnum.OFF;
	}

	return ThreeStateToggleEnum.PARTIAL;
}

export function updateNotificationListFromItem(
	itemToUpdate: string,
	list: GroupedSettings[]
) {
	return list.map((group) => {
		const items = group.item.map((item) => {
			if (item.abrv === itemToUpdate) {
				item.isActive = !item.isActive;
			}
			return item;
		});
		return { ...group, item: items, value: getGroupValueFromItems(group) };
	});
}

export function updateNotificationListFromGroup(
	groupAbrv: string,
	newGroupValue: string,
	list: GroupedSettings[]
) {
	return list.map((group) => {
		if (group.groupName == groupAbrv) {
			if (newGroupValue == ThreeStateToggleEnum.ON) {
				group.item.forEach((item) => (item.isActive = true));
				return { ...group, value: newGroupValue };
			}

			if (newGroupValue == ThreeStateToggleEnum.OFF) {
				group.item.forEach((item) => (item.isActive = false));
				return { ...group, value: newGroupValue };
			}
		}
		return group;
	});
}

export function createNotificationSettingsDataForSubmit(
	formValues: GroupedSettings[],
	buffer: Set<string>
) {
	return formValues.flatMap((group) =>
		group.item
			.filter((item) => buffer.has(item.abrv))
			.map((item) => ({
				channelId: item.id,
				isActive: item.isActive,
				agencyId: item.agencyId,
				userId: item.userId,
			}))
	);
}

export function getGroupItemsAbrvWithDiffValueFromGroup(
	groupAbrv: string,
	newGroupValue: string,
	formData: GroupedSettings[]
) {
	const updatedFields: string[] = [];

	formData.forEach((group) => {
		if (group.groupName == groupAbrv) {
			group.item.forEach((item) => {
				if (
					(newGroupValue == ThreeStateToggleEnum.ON &&
						item.isActive == false) ||
					(newGroupValue == ThreeStateToggleEnum.OFF &&
						item.isActive == true)
				) {
					updatedFields.push(item.abrv);
				}
			});
		}
	});

	return updatedFields;
}
